/**
 * TriggerNode - Automation flow trigger node component
 *
 * Supports two visual states:
 * - Collapsed: Rectangular card matching step nodes (icon + label + chevron, click to expand)
 * - Expanded: Rectangular card with full configuration UI
 */

import { type NodeProps, Handle, Position, useReactFlow } from '@xyflow/react';
import { css } from 'aphrodite';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { accessibilityStyles } from '../../../styles/styles';
import * as Api from '@ViewModels';
import { AutomationTriggerIcon } from '../../../components/svgs/icons/AutomationTriggerIcon';
import type { TriggerNodeData } from '../types/triggerNodes';
import { getTriggerLabel, normalizeTriggerType } from '../utils/convertTemplateToFlow';
import { TriggerSettingsRenderer } from './renderers/TriggerSettingsRenderer';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import { useNodeHeightMeasurement } from '../hooks/useNodeHeightMeasurement';
import { styleSheet as triggerStyles } from './triggerNodeComponentStyles';

function CollapseIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M12 10L8 6L4 10' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

function ExpandIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

export function TriggerNode({ data, selected }: NodeProps) {
	const nodeData = data as TriggerNodeData;
	const trigger = nodeData.automationTrigger;
	const normalizedTrigger = trigger ? normalizeTriggerType(trigger) : undefined;
	const triggerLabel = trigger ? getTriggerLabel(trigger) : nodeData.label || 'Trigger';

	// For manual automations (no trigger object), create a synthetic trigger for icon rendering
	const iconTrigger = normalizedTrigger ?? ({ _type: Api.AutomationTriggerType.Manual } as Api.IAutomationTrigger);

	const { isReadOnly, updateNodeData } = useAutomationFlowContext();
	const { setNodes } = useReactFlow();

	// Local state for expanded/collapsed
	const [isExpanded, setIsExpanded] = useState(nodeData.isExpanded || false);

	// Sync local state when parent updates data.isExpanded (e.g., Ctrl+E bulk toggle).
	useEffect(() => {
		setIsExpanded(nodeData.isExpanded || false);
	}, [nodeData.isExpanded]);

	const contentRef = useRef<HTMLDivElement>(null);

	// Fall back to direct node updates when no canvas callback is available.
	const heightUpdateCallback = useMemo(
		() =>
			updateNodeData ??
			((nodeId: string, nextData: Record<string, unknown>) => {
				setNodes(nodes =>
					nodes.map(node => (node.id === nodeId ? { ...node, data: { ...node.data, ...nextData } } : node))
				);
			}),
		[updateNodeData, setNodes]
	);

	useNodeHeightMeasurement(
		contentRef,
		'trigger',
		isExpanded,
		typeof nodeData.estimatedHeight === 'number' ? nodeData.estimatedHeight : null,
		heightUpdateCallback
	);

	// Determine if editable
	const isNodeReadOnly = isReadOnly || nodeData.isReadOnly;

	// Handle expand
	const handleExpand = useCallback(() => {
		if (!isNodeReadOnly) {
			setIsExpanded(true);
			updateNodeData?.('trigger', { isExpanded: true });
		}
	}, [isNodeReadOnly, updateNodeData]);

	// Handle collapse
	const handleCollapse = useCallback(() => {
		setIsExpanded(false);
		updateNodeData?.('trigger', { isExpanded: false });
	}, [updateNodeData]);

	// The cards deliberately have no keyboard toggle: key events bubble up from inputs
	// rendered inside (e.g. the tag trigger search field), so an Enter/Space hotkey
	// collapses the card mid-typing. Keyboard access is the explicit expand/collapse
	// buttons; the wrapper divs only handle clicks.
	const ariaLabel = `Trigger: ${triggerLabel}${isExpanded ? ', expanded' : ', click to configure'}`;

	if (isExpanded) {
		return (
			<div className={css(triggerStyles.wrapper)}>
				<div
					ref={contentRef}
					className={`${css(triggerStyles.expandedCard, selected && triggerStyles.expandedCardSelected)} nopan nodrag`}
					role='group'
					aria-label={ariaLabel}
					onPointerDown={e => e.stopPropagation()}
				>
					{/* Source handle at bottom center */}
					<Handle type='source' position={Position.Bottom} id='default' className={css(triggerStyles.handleExpanded)} />

					{/* Header */}
					<div className={css(triggerStyles.expandedHeader)}>
						<div className={css(triggerStyles.headerLeft)}>
							<div className={css(triggerStyles.headerIcon)}>
								<AutomationTriggerIcon trigger={iconTrigger} />
							</div>
							<span className={css(triggerStyles.headerTitle)} title={triggerLabel}>
								{triggerLabel}
							</span>
							{isNodeReadOnly && (
								<div className={css(triggerStyles.readOnlyBadge)} title='Read-only mode' aria-label='Read-only mode'>
									READ-ONLY
								</div>
							)}
						</div>
						<button
							className={css(triggerStyles.collapseButton)}
							onClick={handleCollapse}
							aria-label='Collapse trigger settings'
							title='Collapse'
						>
							<CollapseIcon />
						</button>
					</div>

					{/* Settings content */}
					<div className={css(triggerStyles.expandedContent)}>
						<TriggerSettingsRenderer data={nodeData} readOnly={isNodeReadOnly} />
					</div>
				</div>

				<span id='trigger-description' className={css(accessibilityStyles.srOnly)}>
					Starting point of the automation flow - click to configure trigger settings
				</span>
			</div>
		);
	}

	// Collapsed state (card matching step nodes)
	return (
		<div className={css(triggerStyles.wrapper)}>
			<div
				role='group'
				className={css(triggerStyles.collapsedCard, selected && triggerStyles.collapsedCardSelected)}
				aria-label={ariaLabel}
				aria-describedby='trigger-description'
				onClick={handleExpand}
			>
				{/* Source handle at bottom center */}
				<Handle type='source' position={Position.Bottom} id='default' className={css(triggerStyles.handle)} />

				<div className={css(triggerStyles.compactHeader)}>
					{/* Icon */}
					<div className={css(triggerStyles.iconContainer)}>
						<AutomationTriggerIcon trigger={iconTrigger} />
					</div>

					{/* Label */}
					<span className={css(triggerStyles.label)} title={triggerLabel}>
						{triggerLabel}
					</span>

					{/* Expand button */}
					{!isNodeReadOnly && (
						<button
							className={css(triggerStyles.expandButton)}
							onClick={e => {
								e.stopPropagation();
								handleExpand();
							}}
							aria-label='Expand trigger settings'
							title='Expand'
						>
							<ExpandIcon />
						</button>
					)}
				</div>
			</div>

			<span id='trigger-description' className={css(accessibilityStyles.srOnly)}>
				Starting point of the automation flow - click to configure trigger settings
			</span>
		</div>
	);
}
